/**
 * Which leader pills are selected, read from and written to the address (FR-D07a).
 *
 * One leader keeps its own path, `/delivery/leader/:leaderKey` — the address every saved link and
 * every "back to Kapil Rao's team" control already uses, and the scope that reads that leader's
 * own stored figures, trend arrows included. Two or more are `/delivery?leader=A&leader=B`, a
 * repeated parameter rather than one joined string, because a leader key is a name and a name
 * can hold any separator short enough to be pleasant in an address.
 *
 * ⚠ **Mirrors `normalizeLeaderKey` / `leaderSetKey` in `backend/src/domain/delivery.ts`.** The
 * server re-normalises everything it is sent, so a divergence here costs a cache miss rather than
 * a wrong figure — but the pills read the selection off this module, and a key normalised two
 * ways is a pill that shows unselected while its leader's division is on screen.
 */

export const LEADER_PARAM = 'leader';

/** `UPPER(collapse-whitespace(TRIM(name)))` — no key can contain a newline. */
export function normalizeLeaderKey(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
}

/** Normalised, de-duplicated and sorted, so one selection has exactly one spelling. */
export function canonicalLeaderKeys(keys: readonly string[]): string[] {
  return [...new Set(keys.map(normalizeLeaderKey).filter(Boolean))].sort();
}

/** The `leaders` scope key the API takes: the canonical keys, newline-joined. */
export function leaderSetKey(keys: readonly string[]): string {
  return canonicalLeaderKeys(keys).join('\n');
}

/** Where a selection lives. Empty is the all-managers page. */
export function leaderSelectionPath(keys: readonly string[]): string {
  const canonical = canonicalLeaderKeys(keys);
  if (canonical.length === 0) return '/delivery';
  if (canonical.length === 1) return `/delivery/leader/${encodeURIComponent(canonical[0]!)}`;
  const query = new URLSearchParams(canonical.map((key) => [LEADER_PARAM, key]));
  return `/delivery?${query.toString()}`;
}
